<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  name: 'AvailabilitySettings',
  components: { NextButton },
  props: {
    inbox: { type: Object, default: () => ({}) },
  },
  emits: ['go-to-ticket-form', 'go-to-queue'],
  data() {
    return {
      chatMode: 'always', // 'always' | 'agents_accepting'
      isSaving: false,
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
  },
  watch: {
    inbox: {
      immediate: true,
      handler() {
        this.loadFromInbox();
      },
    },
  },
  methods: {
    loadFromInbox() {
      const cfg = this.inbox.widget_theme_config || {};
      this.chatMode = cfg.chatAvailabilityMode || 'always';
    },
    async save() {
      this.isSaving = true;
      try {
        const existingTheme = this.inbox.widget_theme_config || {};
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          channel: {
            widget_theme_config: {
              ...existingTheme,
              chatAvailabilityMode: this.chatMode,
            },
          },
        });
        useAlert('Availability settings saved');
      } catch (e) {
        useAlert('Unable to save availability settings');
      } finally {
        this.isSaving = false;
      }
    },
    cancel() {
      this.loadFromInbox();
    },
  },
};
</script>

<template>
  <div class="avs-page">
    <div class="avs-body">
      <!-- ── Header ── -->
      <div class="avs-header">
        <h2 class="avs-title">Availability</h2>
        <p class="avs-desc">
          Control when customers can start a chat and what happens when your
          team is unavailable.
        </p>
      </div>

      <!-- ── Section: Customers can start a chat ── -->
      <section class="avs-section">
        <h3 class="avs-section-title">Customers can start a chat</h3>

        <div class="avs-options">
          <!-- Option: Always -->
          <label
            class="avs-option"
            :class="{ 'avs-option--selected': chatMode === 'always' }"
            @click="chatMode = 'always'"
          >
            <span class="avs-option__radio">
              <input
                v-model="chatMode"
                type="radio"
                value="always"
                class="avs-radio-input"
              />
              <span
                class="avs-radio-custom"
                :class="{ 'avs-radio-custom--active': chatMode === 'always' }"
              />
            </span>
            <span class="avs-option__body">
              <span class="avs-option__title">Always</span>
              <ul class="avs-option__list">
                <li>Customers can send messages even if you're away.</li>
                <li>You'll see their messages as unassigned chats.</li>
                <li>Customers will get an email with your reply.</li>
                <li>
                  Chat history is available for both agents and customers.
                </li>
              </ul>
            </span>
          </label>

          <!-- Option: Only when agents accepting -->
          <label
            class="avs-option"
            :class="{ 'avs-option--selected': chatMode === 'agents_accepting' }"
            @click="chatMode = 'agents_accepting'"
          >
            <span class="avs-option__radio">
              <input
                v-model="chatMode"
                type="radio"
                value="agents_accepting"
                class="avs-radio-input"
              />
              <span
                class="avs-radio-custom"
                :class="{
                  'avs-radio-custom--active': chatMode === 'agents_accepting',
                }"
              />
            </span>
            <span class="avs-option__body">
              <span class="avs-option__title">Only when agents are accepting chats</span>
              <ul class="avs-option__list">
                <li>Customers can leave a ticket when you're away.</li>
                <li>You can handle tickets once you're available.</li>
                <li>Customers can see the chat history.</li>
              </ul>
            </span>
          </label>
        </div>
      </section>

      <!-- ── Section: What happens when ── -->
      <section class="avs-section">
        <h3 class="avs-section-title">What happens when</h3>

        <!-- Agents are away -->
        <div class="avs-event-row">
          <div class="avs-event-row__left">
            <span class="avs-event-icon">
              <!-- away icon -->
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </span>
            <div>
              <p class="avs-event-title">Agents are away</p>
              <p class="avs-event-desc">
                Customers will see a ticket form depending on group settings.
              </p>
            </div>
          </div>
          <button
            class="avs-customize-btn"
            type="button"
            @click="$emit('go-to-ticket-form')"
          >
            Customize
          </button>
        </div>

        <!-- Customers in queue -->
        <div class="avs-event-row">
          <div class="avs-event-row__left">
            <span class="avs-event-icon">
              <!-- queue icon -->
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
            </span>
            <div>
              <p class="avs-event-title">Customers get in a queue</p>
              <p class="avs-event-desc">Customers will see queue info.</p>
            </div>
          </div>
          <button
            class="avs-customize-btn"
            type="button"
            @click="$emit('go-to-queue')"
          >
            Customize
          </button>
        </div>
      </section>

      <!-- ── Footer ── -->
      <div class="avs-footer">
        <button class="avs-cancel-btn" type="button" @click="cancel">
          Cancel
        </button>
        <NextButton
          type="button"
          label="Save changes"
          :is-loading="isSaving"
          :disabled="isSaving"
          @click="save"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.avs-page {
  height: 100%;
  overflow-y: auto;
  background: #ffffff;
}

.avs-body {
  max-width: 720px;
  margin: 0;
  padding: 32px 32px 80px;
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.avs-header {
}
.avs-title {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 6px;
}
.avs-desc {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.55);
  margin: 0;
  line-height: 1.5;
}

.avs-section {
}
.avs-section-title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 14px;
}

.avs-options {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
}

.avs-option {
  flex: 1 1 260px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 18px 20px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
  background: #fff;
  user-select: none;
}
.avs-option--selected {
  border-color: #4384f5;
  box-shadow: 0 0 0 3px rgba(67, 132, 245, 0.12);
}
.avs-option:hover:not(.avs-option--selected) {
  border-color: #d1d5db;
}

.avs-option__radio {
  flex-shrink: 0;
  position: relative;
  width: 18px;
  height: 18px;
  margin-top: 1px;
}
.avs-radio-input {
  position: absolute;
  opacity: 0;
  inset: 0;
  cursor: pointer;
  margin: 0;
}
.avs-radio-custom {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  background: #fff;
  pointer-events: none;
  transition: border-color 0.15s;
}
.avs-radio-custom--active {
  border-color: #4384f5;
}
.avs-radio-custom--active::after {
  content: '';
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4384f5;
}

.avs-option__body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.avs-option__title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}
.avs-option__list {
  list-style: disc;
  padding-left: 16px;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.avs-option__list li {
  font-size: 12.5px;
  color: rgba(0, 0, 0, 0.6);
  line-height: 1.4;
}

/* Event rows */
.avs-event-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  margin-bottom: 10px;
  background: #fff;
}
.avs-event-row__left {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}
.avs-event-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f3f4f6;
  color: #6b7280;
  flex-shrink: 0;
}
.avs-event-title {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 2px;
}
.avs-event-desc {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
  margin: 0;
}

.avs-customize-btn {
  flex-shrink: 0;
  padding: 7px 18px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;
  white-space: nowrap;
}
.avs-customize-btn:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

/* Footer */
.avs-footer {
  display: flex;
  gap: 12px;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
}
.avs-cancel-btn {
  padding: 9px 20px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  transition: background 0.15s;
}
.avs-cancel-btn:hover {
  background: #f9fafb;
}
</style>
